import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Loader2, Printer } from "lucide-react";
import { printClosing } from "@/lib/printShift";

export default function ShiftCloseModal({ open, onClose, onClosed, storeName }) {
  const [loading, setLoading] = useState(true);
  const [shift, setShift] = useState(null);
  const [summary, setSummary] = useState(null);
  const [cash, setCash] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setCash(""); setShift(null); setSummary(null);
    api.get("/shifts/summary")
      .then((r) => { setShift(r.data.shift); setSummary(r.data.summary); })
      .catch((e) => { toast.error(apiError(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [open]);

  const expected = summary?.cash_expected || 0;
  const selisih = (Number(cash || 0)) - expected;

  const doClose = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/shifts/close", { cash_counted: Number(cash || 0) });
      toast.success("Shift ditutup");
      printClosing(data, data.summary, storeName);
      onClosed(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="shift-close-dialog" aria-describedby={undefined} className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-md">
        <DialogHeader><DialogTitle>Tutup Shift</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : summary && (
          <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 divide-y divide-slate-200 text-sm">
              <Row label="Jumlah Transaksi" value={summary.tx_count} mono />
              <Row label="Total Penjualan" value={rupiah(summary.revenue)} mono />
              <Row label="Tunai" value={rupiah(summary.tunai)} mono />
              <Row label="Transfer" value={rupiah(summary.transfer)} mono />
              <Row label="QRIS" value={rupiah(summary.qris)} mono />
              <Row label="Kembalian" value={rupiah(summary.change)} mono />
              <Row label="Piutang (baru)" value={rupiah(summary.piutang)} mono cls="text-red-600" />
              <Row label="Pelunasan Piutang" value={rupiah(summary.pelunasan)} mono cls="text-emerald-600" />
              <Row label="Kas Seharusnya" value={rupiah(expected)} mono cls="text-amber-600 font-bold" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Hitung Fisik Kas (uang di laci)</label>
              <MoneyInput
                data-testid="shift-cash-input"
                value={cash}
                onValueChange={setCash}
                placeholder="0"
                className="mt-1 h-11 text-right font-mono bg-[#F7F9FB] border-slate-300 text-lg"
              />
            </div>
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${selisih === 0 ? "bg-emerald-500/10 text-emerald-600" : selisih > 0 ? "bg-blue-500/10 text-blue-300" : "bg-red-500/10 text-red-500"}`}>
              <span className="text-sm font-semibold">Selisih</span>
              <span data-testid="shift-selisih" className="font-mono font-bold">{rupiah(selisih)}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-300 bg-[#F7F9FB] hover:bg-slate-200">Batal</Button>
          <Button data-testid="shift-close-confirm" onClick={doClose} disabled={saving || loading} className="bg-red-600 hover:bg-red-700 text-white gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Tutup & Cetak
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, mono, cls = "" }) {
  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${cls}`}>{value}</span>
    </div>
  );
}
